import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useParams, Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import api, { handleApiError } from "../../../helpers/api";
import { toast, ToastContainer } from "react-toastify";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Thumbs } from "swiper";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import classnames from "classnames";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";

SwiperCore.use([Navigation, Thumbs]);

const PricingWidgetList = ({ pricingDetails }) => {
  return (
      <Col lg={3} sm={6}>
        <div className="p-2 border border-dashed rounded">
          <div className="d-flex align-items-center">
            <div className="avatar-sm me-2">
              <div className="avatar-title rounded bg-transparent text-success fs-24">
                <i className={pricingDetails.icon}></i>
              </div>
            </div>
            <div className="flex-grow-1">
              <p className="text-muted mb-1">{pricingDetails.label} </p>
              <h5 className="mb-0">{pricingDetails.labelDetail}</h5>
            </div>
          </div>
        </div>
      </Col>
  );
};

const EcommerceProductDetail = ({ navigate }) => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [specDefs, setSpecDefs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [customActiveTab, setCustomActiveTab] = useState("1");

  // Fetch product and spec definitions
  useEffect(() => {
    const fetchData = async () => {
      try {
        const productResponse = await api.get(`/products/${id}`);
        const productData = productResponse.data.data;
        setProduct(productData);
        setTotalPrice(productData.price || 0);

        // Fetch specDefinitions using product.category._id
        const categoryId = productData.category?._id;
        let specDefsResponse = await api.get(`/specDefinitions?category=${categoryId}`);
        setSpecDefs(specDefsResponse.data.data || []);
        if (specDefs.length === 0 && productData.category?.parent) {
          const parentCategoryId = productData.category.parent;
          specDefsResponse = await api.get(`/specDefinitions?category=${parentCategoryId}`);
          setSpecDefs( specDefsResponse.data.data || [])
        }


        // Initialize selectedConfig with included options
        if (productData.customizable) {
          const initialConfig = {};
          productData.usedSpecs.forEach(spec => {
            initialConfig[spec.name] = spec.options
              .filter(opt => opt.included)
              .map(opt => opt.name);
          });
          setSelectedConfig(initialConfig);
        }
        setLoading(false);
      }   catch (error) {
        handleApiError(error, () => {}, navigate);
        toast.error(error.response?.data?.message || "Failed to fetch data");
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Update total price when selectedConfig changes
 useEffect(() => {
    if (product?.customizable && specDefs.length > 0) {
      const configCost = specDefs.reduce((total, specDef) => {
        const selectedOptions = selectedConfig[specDef.name] || [];
        const cost = selectedOptions.reduce((sum, optionName) => {
          // Check price in specDef.options
          const option = specDef.options.find(opt => opt.name === optionName);
          const price = option?.price;

          // Fallback to product.usedSpecs if not found in specDef
          const fallbackPrice = product.usedSpecs
            .find(s => s.name === specDef.name)?.options
            .find(o => o.name === optionName)?.price;

          return sum + (price || fallbackPrice || 0);
        }, 0);
        return total + cost;
      }, 0);
      setTotalPrice((product.price || 0) + configCost);
    }
  }, [selectedConfig, product, specDefs]);

  // Handle option selection
  const handleOptionChange = (specName, optionName, isMultiple, checked) => {
    setSelectedConfig(prev => {
      const currentSelections = prev[specName] || [];
      let newSelections;

      if (isMultiple) {
        newSelections = checked
            ? [...currentSelections, optionName]
            : currentSelections.filter(opt => opt !== optionName);
      } else {
        newSelections = checked ? [optionName] : [];
      }

      // Apply maxItems constraint
      const specDef = specDefs.find(def => def.name === specName);
      if (specDef?.max && newSelections.length > specDef.max) {
        toast.error(`Maximum ${specDef.max} options allowed for ${specName}`);
        return prev;
      }

      return { ...prev, [specName]: newSelections };
    });
  };

  // Handle delete product
  const handleDeleteProduct = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted successfully");
      setDeleteModal(false);
      window.location.href = "/apps-ecommerce-products";
    } catch (error) {
      handleApiError(error, () => {}, navigate);
      toast.error(error.response?.data?.message || "Failed to delete product");
    } finally {
      setIsDeleting(false);
    }
  };

  // Tab toggle
  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab);
    }
  };

  // Parse and sanitize rich text description
// Parse and sanitize rich text description
  const renderDescription = (html) => {
    const sanitizedHtml = DOMPurify.sanitize(html, { ADD_TAGS: ["oembed"], ADD_ATTR: ["url"] });
    return parse(sanitizedHtml, {
      replace: (domNode) => {
        if (domNode.name === "oembed" && domNode.attribs.url) {
          const url = domNode.attribs.url;
          const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/;
          const match = url.match(youtubeRegex);
          if (match) {
            const videoId = match[1];
            return (
                <div className="ratio ratio-16x9" style={{ maxWidth: "560px", margin: "1rem 0" }}>
                  <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title="YouTube video"
                      allowFullScreen
                      className="rounded"
                  ></iframe>
                </div>
            );
          }
        }
      },
    });
  };
  if (loading) {
    return (
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title="Product Details" pageTitle="Ecommerce" />
            <div className="text-center py-5">
              <h5>Loading...</h5>
            </div>
          </Container>
        </div>
    );
  }

  if (!product) {
    return (
        <div className="page-content">
          <Container fluid>
            <BreadCrumb title="Product Details" pageTitle="Ecommerce" />
            <div className="text-center py-5">
              <h5>Product not found</h5>
            </div>
          </Container>
        </div>
    );
  }

  // Pricing widgets data
  const productDetailsWidgets = [
    { label: "Price", labelDetail: product.customizable ? `${totalPrice.toFixed(2)} TND` : `${product.price?.toFixed(2)} TND`, icon: "ri-money-dollar-circle-line" },
    { label: "Stock", labelDetail: product.stock || 0, icon: "ri-stack-line" },
    { label: "Category", labelDetail: product.category?.name || "N/A", icon: "ri-price-tag-3-line" },
    { label: "Brand", labelDetail: product.brand || "N/A", icon: "ri-user-3-line" },
    { label: "Sales", labelDetail: product.sold || 0, icon: "ri-shopping-cart-line" },

  ];

  // Features from specs for non-customizable products
  const features = product.customizable
      ? product.usedSpecs?.map(spec => ({
    label: spec.name,
    value: spec.options.map(opt => `${opt.name}${opt.included ? ' (Included)' : ''}`).join(', ') || 'None',
  })) || []
      : product.specs
          ? Object.entries(product.specs).map(([key, value]) => ({
            label: key,
            value: Array.isArray(value) ? value.join(', ') : value,
          }))
          : [];

  document.title = `${product.name} | Confidoo`;

  return (
      <div className="page-content">
        <ToastContainer closeButton={false} limit={1} />
        <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteProduct}
            onCloseClick={() => setDeleteModal(false)}
            deleteButtonDisabled={isDeleting}
        />
        <Container fluid>
          <BreadCrumb title="Product Details" pageTitle="Ecommerce" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="card-title mb-0">{product.name}</h4>

                    <div className="d-flex gap-2">
                      <Link to={`/apps-ecommerce-edit-product/${product._id}`}>
                        <Button color="success" className="btn btn-success">
                          <i className="ri-pencil-fill align-bottom me-1"></i> Edit
                        </Button>
                      </Link>
                      <Button
                          color="danger"
                          className="btn btn-danger"
                          onClick={() => setDeleteModal(true)}
                      >
                        <i className="ri-delete-bin-fill align-bottom me-1"></i> Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row className="gx-lg-5">
                    <Col xl={4} md={8} className="mx-auto">
                      <div className="product-img-slider sticky-side-div">
                        <style>
                          {`
                          .zoom-on-hover {
                            transition: transform 0.3s ease;
                          }
                          .zoom-on-hover:hover {
                            transform: scale(1.2);
                          }
                          .swiper-slide {
                            overflow: hidden;
                          }
                        `}
                        </style>
                        <Swiper
                            navigation={true}
                            thumbs={{ swiper: thumbsSwiper }}
                            className="swiper product-thumbnail-slider p-2 rounded bg-light"
                        >
                          <div className="swiper-wrapper">
                            {product.images?.length > 0 ? (
                                product.images.map((image, index) => (
                                    <SwiperSlide key={index}>
                                      <img
                                          src={image.url}
                                          alt={product.name}
                                          className="img-fluid d-block rounded zoom-on-hover"
                                      />
                                    </SwiperSlide>
                                ))
                            ) : (
                                <SwiperSlide>
                                  <img
                                      src="https://via.placeholder.com/300"
                                      alt="Placeholder"
                                      className="img-fluid d-block rounded zoom-on-hover"
                                  />
                                </SwiperSlide>
                            )}
                          </div>
                        </Swiper>
                        <div className="product-nav-slider mt-2">
                          <Swiper
                              onSwiper={setThumbsSwiper}
                              slidesPerView={4}
                              spaceBetween={10}
                              className="swiper product-nav-slider mt-2 overflow-hidden"
                          >
                            <div className="swiper-wrapper">
                              {product.images?.length > 0 ? (
                                  product.images.map((image, index) => (
                                      <SwiperSlide key={index} className="rounded">
                                        <div className="nav-slide-item">
                                          <img
                                              src={image.url}
                                              alt={product.name}
                                              className="img-fluid d-block rounded"
                                          />
                                        </div>
                                      </SwiperSlide>
                                  ))
                              ) : (
                                  <SwiperSlide className="rounded">
                                    <div className="nav-slide-item">
                                      <img
                                          src="https://via.placeholder.com/100"
                                          alt="Placeholder"
                                          className="img-fluid d-block rounded"
                                      />
                                    </div>
                                  </SwiperSlide>
                              )}
                            </div>
                          </Swiper>
                        </div>
                      </div>
                    </Col>
                    <Col xl={8}>
                      <div className="mt-xl-0 mt-5">
                        <div className="d-flex">
                          <div className="flex-grow-1">
                            <h4>{product.name}</h4>
                            <div className="hstack gap-3 flex-wrap">
                              <div className="vr"></div>
                              <div className="text-muted">
                                Created :{" "}
                                <span className="text-body fw-medium">
                                {product.createdAt ? new Date(product.createdAt).toLocaleDateString() : "N/A"}
                              </span>
                              </div>
                              <div className="vr"></div>
                              <div className="text-muted">
                                Last Updated :{" "}
                                <span className="text-body fw-medium">
                                {product.updatedAt ? new Date(product.updatedAt).toLocaleDateString() : "N/A"}
                              </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Row className="mt-4">
                          {productDetailsWidgets.map((pricingDetails, key) => (
                              <PricingWidgetList pricingDetails={pricingDetails} key={key} />
                          ))}
                        </Row>
                        <div className="product-content mt-5">
                          <h5 className="fs-14 mb-3">Product Description :</h5>
                          <Nav tabs className="nav-tabs-custom nav-success">
                            <NavItem>
                              <NavLink
                                  style={{ cursor: "pointer" }}
                                  className={classnames({ active: customActiveTab === "1" })}
                                  onClick={() => toggleCustom("1")}
                              >
                                Specification
                              </NavLink>
                            </NavItem>
                            <NavItem>
                              <NavLink
                                  style={{ cursor: "pointer" }}
                                  className={classnames({ active: customActiveTab === "2" })}
                                  onClick={() => toggleCustom("2")}
                              >
                                Details
                              </NavLink>
                            </NavItem>
                            {product.customizable && (
                                <NavItem>
                                  <NavLink
                                      style={{ cursor: "pointer" }}
                                      className={classnames({ active: customActiveTab === "3" })}
                                      onClick={() => toggleCustom("3")}
                                  >
                                    Configure
                                  </NavLink>
                                </NavItem>
                            )}
                          </Nav>
                          <TabContent activeTab={customActiveTab} className="border border-top-0 p-4">
                            <TabPane id="nav-speci" tabId="1">
                              <Table className="table-borderless table-sm mb-0">
                                <tbody>
                                {features.map((feature, index) => (
                                    <tr key={index}>
                                      <th scope="row" style={{ width: "200px" }}>
                                        {feature.label}
                                      </th>
                                      <td>{feature.value}</td>
                                    </tr>
                                ))}
                                </tbody>
                              </Table>
                            </TabPane>
                            <TabPane id="nav-detail" tabId="2">
                              <div>
                                <h5 className="fs-16 mb-3">{product.name}</h5>
                                {product.description && (
                                    <div className="text-muted fs-14">
                                      {renderDescription(product.description)}
                                    </div>
                                )}
                              </div>
                            </TabPane>
                            {product.customizable && (
                                <TabPane id="nav-config" tabId="3">
                                  <h5 className="fs-16 mb-3">Configure Product</h5>
                                  <div className="mb-4">
                                    <h6>Total Price: {totalPrice.toFixed(2)} TND</h6>
                                  </div>
                                  {product.usedSpecs?.map((spec, index) => {
                                    const specDef = specDefs.find(def => def.name === spec.name);
                                    const isMultiple = specDef?.multiple || false;
                                    const maxItems = specDef?.max || Infinity;
                                    return (
                                        <FormGroup key={index} className="mb-3">
                                          <Label className="fw-bold">{spec.name}</Label>
                                          {spec.options.map((option, optIndex) => (
                                              <FormGroup check key={optIndex} className="ms-3">
                                                <Input
                                                    type={isMultiple ? "checkbox" : "radio"}
                                                    name={isMultiple ? `${spec.name}-${optIndex}` : spec.name}
                                                    id={`${spec.name}-${option.name}`}
                                                    checked={selectedConfig[spec.name]?.includes(option.name) || false}
                                                    onChange={(e) => handleOptionChange(spec.name, option.name, isMultiple, e.target.checked)}
                                                    disabled={option.included && selectedConfig[spec.name]?.includes(option.name)}
                                                />
                                                <Label check for={`${spec.name}-${option.name}`} className="ms-1">
{option.name} {option.included
                                                    ? "(Included)"
                                                    : `(+${specDef?.options.find(o => o.name === option.name)?.price 
                                                        || product.usedSpecs.find(s => s.name === specDef?.name)?.options.find(o => o.name === option.name)?.price 
                                                        || 0} TND)`}
                                                                                                </Label>
                                              </FormGroup>
                                          ))}
                                          {maxItems !== Infinity && (
                                              <p className="text-muted mt-1">Max {maxItems} option(s) allowed</p>
                                          )}
                                        </FormGroup>
                                    );
                                  })}
                                </TabPane>
                            )}
                          </TabContent>
                        </div></div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
  );
};

export default EcommerceProductDetail;